/** @format */

import Footer from '@/components/shared/Footer';

import { UINavbar } from '@/components/ui/Navbar';
import { selectorLocale, selectorRoomIndex, selectorRoomListType } from '@/ducks/general/selectors';
import { GTMproductDetail } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { selectorFilters } from '@/ducks/listing/selectors';
import { TRoom } from '@/ducks/room/declarations';
import { utilityGetRoomInfo, utilityGetRoomMetaTag } from '@/ducks/room/helpers';
import { BaseHead } from '@/Head';
import { utilityCreateIntlProvider } from '@/i18n/createProvider';
import { useAppTranslation } from '@/i18n/declarations';
import { findLocalizedData } from '@/i18n/utils/find-localized-data';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import '@/scss/style.scss';
import { TPageIntl } from '@/types/general';
import calculateListingUrl from '@/utils/gatsby/calculate-listing-url';
import { TLocale } from '@/utils/gatsby/types';
import { utilityGenerateAlternateLinks } from '@/utils/helpers/generateAlternateLinks';
import { getGtmRoomType } from '@/utils/helpers/getGtmRoomType';
import { utilityIsRoomAvailableFrom } from '@/utils/helpers/isRoomAvailableFrom';
import { utilityGetPrice } from '@/utils/helpers/utilityGetPrice';
import { utilityGTMBuildStringByListingActiveFilters } from '@/utils/helpers/utilityGTMBuildStringByListingActiveFilters';
import { utilityGetJsonLdBreadCrumbs } from '@/utils/jsonLD/getJsonLdBreadCrumbs';
import { HeadProps } from 'gatsby';
import snakeCase from 'lodash/snakeCase';
import React, { lazy, useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { useRoomAvailability } from '../../../@ducks/bookingNew/hooks/useBookingAvailability';
import { useLoader } from '../../../@ducks/bookingNew/hooks/useLoader';
import { TOtherPaths } from '../../../@ducks/general/declarations';
import { NAME_PORTAL_CODE } from '../../../utils/gatsby/constants';
import { pSidData } from '../../../utils/helpers/pSidData';
import MoreOptions from '../../shared/MoreOptionsPDP';
import getJsonLdRoom from './components/getJsonLdRoom';
import RoomGallery from './components/RoomGallery';
import RoomHeader from './components/RoomHeader';
import RoomInfo from './components/RoomInfo/RoomInfo';
import RoomMap from './components/RoomMap';
import { SecondaryNavbar } from './components/SecondaryNav';
import { Container, Navigation } from './index.style';

export const Head = ({ pageContext }: HeadProps<{}, PropsRoomPage['pageContext']>) => {
  const { room, otherPaths } = pageContext;
  const { city } = room;
  const { intl } = utilityCreateIntlProvider(pageContext.intl.language);
  const alternateLinks = utilityGenerateAlternateLinks(otherPaths);
  const { metaDescription, metaTitle } = utilityGetRoomMetaTag(pageContext.intl.language, room);
  const isBrowser = typeof window !== 'undefined';
  const path = isBrowser ? decodeURI(window.location.pathname) : '';
  const jsonLdRoom = getJsonLdRoom(pageContext.intl.language, room);
  const backToListingUrl = calculateListingUrl(city.nameEn, intl, 'all', '');
  const jsonLdBreadCrumbs = utilityGetJsonLdBreadCrumbs(path, pageContext.intl.language, backToListingUrl);

  return (
    <BaseHead>
      <title>{metaTitle}</title>
      {jsonLdRoom && <script type="application/ld+json">{JSON.stringify(jsonLdRoom)}</script>}
      {jsonLdBreadCrumbs && <script type="application/ld+json">{JSON.stringify(jsonLdBreadCrumbs)}</script>}
      <meta name="description" content={metaDescription} />
      {alternateLinks}
    </BaseHead>
  );
};

export interface PropsRoomPage {
  pageContext: {
    room: TRoom;
    otherPaths: TOtherPaths;
    urlDistrict: string | null;
    intl: TPageIntl;
    locale: TLocale;
    timestamp: string;
  };
}

const displayDate = (date: Date) => {
  return (
    date.getFullYear() +
    '-' +
    String(date.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(date.getDate()).padStart(2, '0')
  );
};

const controller = new AbortController();

const formattedDate = displayDate(new Date());

export default ({ pageContext }: PropsRoomPage) => {
  const { room, otherPaths, urlDistrict } = pageContext;
  const { media, code } = room;
  const [showSecondaryNav, setShowSecondaryNav] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { intl } = useAppTranslation();
  const [portalCode = NAME_PORTAL_CODE] = useQueryParam('portalCode', StringParam);
  const { dataPsid } = pSidData();
  const { addLoader } = useLoader('availability');

  const [checkInDate, setCheckInDate] = useState<string>(formattedDate);
  const locale = useAppSelector(selectorLocale);
  const galleryRef = useRef<HTMLDivElement | null>(null);
  const { title } = utilityGetRoomInfo(locale as TLocale, room);
  const { getRoomAvailability } = useRoomAvailability();
  const roomIndex = useAppSelector(selectorRoomIndex);
  const filters = useAppSelector(selectorFilters);
  const roomListType = useAppSelector(selectorRoomListType);

  const updateRoomAvailability = async () => {
    const availability = await getRoomAvailability({
      code,
      checkInDate,
      controller,
      portalCode: portalCode || NAME_PORTAL_CODE,
      commercialConditionsId: dataPsid !== '' ? dataPsid : undefined,
      reduced: true,
    });

    const { getFormattedPrice, getDimension } = utilityGetPrice({
      availability,
      room,
    });

    const isRoomAvailableFrom = utilityIsRoomAvailableFrom(availability);
    const isRoomNotAvailable = availability && (!availability?.availableFrom || availability?.negotiating);

    const localizedEntry = findLocalizedData(intl.locale as TLocale, room);

    const GTMAvailability = () => {
      if (isRoomAvailableFrom) return 'available_on_next_weeks';
      if (isRoomNotAvailable) return 'not_available';
      return 'available';
    };

    const order = filters.orderBy === 'null' || !filters.orderBy ? 'availability_asc' : filters.orderBy;
    const filter = utilityGTMBuildStringByListingActiveFilters(filters);
    const roomType = getGtmRoomType(room.flat.type);

    GTMproductDetail({
      order,
      filter,
      item: {
        item_name: snakeCase(localizedEntry?.title),
        item_id: room.code,
        price: getFormattedPrice(),
        item_brand: `${snakeCase(room.city.nameEn.toLowerCase())}_${snakeCase(room.district.name.toLowerCase())}`,
        item_category: roomType,
        item_list_name: roomListType ?? 'search_result',
        position: roomIndex,
        rate_type: getDimension,
        availability: GTMAvailability(),
      },
    });

    if (availability) {
      const dateToSet = new Date(availability.checkInDates[0]);
      setCheckInDate(displayDate(dateToSet));
    }
  };

  useLayoutEffect(() => {
    addLoader();
  }, []);

  useEffect(() => {
    const execute = async () => {
      const currentController = new AbortController();
      if (code) {
        await updateRoomAvailability();
      }

      if (locale !== pageContext.intl.language) dispatch({ type: 'SET_LOCALE', payload: pageContext.intl.language });
      dispatch({ type: 'SET_CURRENT_PAGE', payload: 'room-page' });

      return () => {
        currentController.abort();
      };
    };

    execute();
  }, [code]);

  useEffect(() => {
    if (!galleryRef.current) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const callback: IntersectionObserverCallback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setShowSecondaryNav(false);
        } else {
          setShowSecondaryNav(true);
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);
    if (galleryRef.current) {
      observer.observe(galleryRef.current);
    }

    return () => {
      if (galleryRef.current) {
        observer.unobserve(galleryRef.current);
      }
      observer.disconnect();
    };
  }, [galleryRef]);

  return (
    <>
      <Navigation>
        <UINavbar page="room" className="room-page-header" otherPaths={otherPaths} />
        <SecondaryNavbar showSecondaryNav={showSecondaryNav} />
      </Navigation>
      <Container>
        {/* Only desktop */}
        <RoomHeader title={title} room={room} />

        <div ref={galleryRef} id="galleryAnchor">
          <RoomGallery photos={media} room={room} />
        </div>
        <div>
          <RoomInfo locale={pageContext.intl.language} room={room} />
        </div>
        <div id="mapAnchor">
          <RoomMap room={room} url={urlDistrict} />
        </div>
        <MoreOptions language={pageContext.intl.language} checkInDate={checkInDate} room={room} />
      </Container>
      <Footer />
    </>
  );
};
